import { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FilterChip, FilterMenuItem } from '../filters/FilterChip';
import {
  DEFAULT_EXECUTION_FILTERS,
  type ExecutionFiltersState,
  type ExecutionStatusFilter,
  type VisibilityScoreFilter,
} from '../../utils/execution-filters';

const STATUS_OPTIONS: { id: ExecutionStatusFilter; label: string }[] = [
  { id: 'all', label: 'همه وضعیت‌ها' },
  { id: 'executed', label: 'اجرا شده' },
  { id: 'running', label: 'در حال اجرا' },
  { id: 'queued', label: 'در صف' },
  { id: 'failed', label: 'ناموفق' },
  { id: 'cancelled', label: 'لغو شده' },
];

const VISIBILITY_OPTIONS: { id: VisibilityScoreFilter; label: string }[] = [
  { id: 'all', label: 'همه دیده‌شدن' },
  { id: '0-20', label: '0–20%' },
  { id: '20-40', label: '20–40%' },
  { id: '40-60', label: '40–60%' },
  { id: '60-80', label: '60–80%' },
  { id: '80-100', label: '80–100%' },
];

function BrandFilterInput({
  value,
  suggestions,
  onChange,
}: {
  value: string;
  suggestions: string[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const query = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query) return suggestions;
    return suggestions.filter((name) => name.toLowerCase().includes(query));
  }, [suggestions, query]);

  return (
    <div className="position-relative" style={{ minWidth: '9rem', maxWidth: '12rem' }}>
      <FontAwesomeIcon
        icon="tag"
        className="position-absolute top-50 translate-middle-y text-400"
        style={{ insetInlineStart: '0.75rem', pointerEvents: 'none', zIndex: 1 }}
      />
      <Form.Control
        type="search"
        size="sm"
        value={value}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        placeholder="برند…"
        className="rounded-pill border-300"
        style={{ paddingInlineStart: '2.25rem' }}
      />
      {open && matches.length > 0 && (
        <div
          className="dropdown-menu show py-1 mt-1"
          style={{ maxHeight: 240, overflowY: 'auto', minWidth: '100%', maxWidth: '16rem' }}
        >
          {matches.map((name) => (
            <button
              key={name}
              type="button"
              className="dropdown-item text-truncate fs--1"
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(name);
                setOpen(false);
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExecutionStatusVisibilityFilters({
  filters,
  onChange,
}: {
  filters: ExecutionFiltersState;
  onChange: (next: ExecutionFiltersState) => void;
}) {
  const statusLabel =
    STATUS_OPTIONS.find((option) => option.id === filters.status)?.label ?? 'همه وضعیت‌ها';
  const visibilityLabel =
    VISIBILITY_OPTIONS.find((option) => option.id === filters.visibility)?.label ??
    'همه دیده‌شدن';

  return (
    <>
      <FilterChip
        icon={<FontAwesomeIcon icon="check-circle" className="fs--2" />}
        label={statusLabel}
        active={filters.status !== 'all'}
        align="end"
      >
        {STATUS_OPTIONS.map((option) => (
          <FilterMenuItem
            key={option.id}
            selected={filters.status === option.id}
            onClick={() => onChange({ ...filters, status: option.id })}
          >
            {option.label}
          </FilterMenuItem>
        ))}
      </FilterChip>

      <FilterChip
        icon={<FontAwesomeIcon icon="chart-line" className="fs--2" />}
        label={visibilityLabel}
        active={filters.visibility !== 'all'}
        align="end"
      >
        {VISIBILITY_OPTIONS.map((option) => (
          <FilterMenuItem
            key={option.id}
            selected={filters.visibility === option.id}
            onClick={() => onChange({ ...filters, visibility: option.id })}
          >
            {option.label}
          </FilterMenuItem>
        ))}
      </FilterChip>
    </>
  );
}

export function ExecutionFiltersBar({
  filters,
  onChange,
  brandSuggestions,
}: {
  filters: ExecutionFiltersState;
  onChange: (next: ExecutionFiltersState) => void;
  brandSuggestions?: string[];
  /** @deprecated Card embedding removed; kept for call-site compatibility. */
  embedded?: boolean;
}) {
  const hasActiveFilters =
    filters.search.trim() !== '' ||
    filters.status !== 'all' ||
    filters.provider !== 'all' ||
    filters.visibility !== 'all' ||
    filters.brand.trim() !== '';

  return (
    <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
      <div className="position-relative flex-grow-1" style={{ minWidth: '16rem', maxWidth: '24rem' }}>
        <FontAwesomeIcon
          icon="search"
          className="position-absolute top-50 translate-middle-y text-400"
          style={{ insetInlineStart: '0.75rem', pointerEvents: 'none' }}
        />
        <Form.Control
          type="search"
          size="sm"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="جستجو بر اساس پرامپت، برند یا پرووایدر…"
          className="rounded-pill border-300"
          style={{ paddingInlineStart: '2.25rem' }}
        />
      </div>

      <BrandFilterInput
        value={filters.brand}
        suggestions={brandSuggestions ?? []}
        onChange={(brand) => onChange({ ...filters, brand })}
      />

      {hasActiveFilters && (
        <Button
          type="button"
          size="sm"
          variant="link"
          className="text-decoration-none text-500 px-1"
          onClick={() => onChange(DEFAULT_EXECUTION_FILTERS)}
        >
          پاک کردن
        </Button>
      )}
    </div>
  );
}

export { DEFAULT_EXECUTION_FILTERS, type ExecutionFiltersState };
export {
  matchesSearch,
  matchesStatusFilter,
  matchesVisibilityRange,
  normalizeSearchQuery,
  providerMatches,
} from '../../utils/execution-filters';
